import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { flattenMarkdownTokens, parseTableCellMarkdown, parseMarkdown } from "./markdownParser";
import {
  SECTION_TITLES,
  getDocumentSections,
  formatTableHeader,
  formatArrayCellContent,
} from "./documentHelpers";

// Format Impact column for PDF - add "Impact: High" prefix for clarity
const formatImpactForPDF = (value) => {
  if (!value || typeof value !== 'string') return value;
  
  const lines = value.split('\n').filter(line => line.trim());
  if (lines.length === 0) return value;
  
  // Remove standalone "High/Medium/Low" first line if it exists
  const firstLine = lines[0].trim();
  if (firstLine.match(/^(High|Medium|Low)$/)) {
    lines.shift(); // Remove it
    return `Impact: ${firstLine}\n${lines.join('\n')}`;
  }
  
  // No standalone level, just add "Impact: " prefix
  return `Impact:\n${lines.join('\n')}`;
};

// Sanitize text for PDF export (remove emoji that don't render properly)
const sanitizeTextForPDF = (text) => {
  if (!text) return text;
  
  let sanitized = text
    .replace(/⚠️\s*External:/g, '[External]:')
    .replace(/✅\s*TUI:/g, '[TUI]:')
    .replace(/⚠️/g, '[!]')
    .replace(/✅/g, '[✓]');
  
  return sanitized;
};

// PDF-specific token processing - no links in PDF
const processInlineTokensForPDF = (tokens) => {
  const flattened = flattenMarkdownTokens(tokens);
  return flattened.map((token) => ({
    text: sanitizeTextForPDF(token.text),
    bold: token.bold,
    italic: token.italic,
    code: token.type === "code",
    // link: token.link, // REMOVED - no clickable links in PDF
    strike: token.strike,
  }));
};

// Parse markdown and extract text with formatting info
const parseMarkdownForPDF = (markdown) => {
  if (!markdown || markdown.trim().length === 0) {
    return [];
  }

  try {
    const tokens = parseMarkdown(markdown);
    const segments = [];

    tokens.forEach((token) => {
      switch (token.type) {
        case "heading":
          if (token.tokens && token.tokens.length > 0) {
            segments.push({
              type: "heading",
              level: token.depth,
              content: processInlineTokensForPDF(token.tokens),
            });
          } else if (token.text) {
            segments.push({
              type: "heading",
              level: token.depth,
              content: [{ text: token.text }],
            });
          }
          break;

        case "paragraph":
          if (token.tokens && token.tokens.length > 0) {
            segments.push({
              type: "paragraph",
              content: processInlineTokensForPDF(token.tokens),
            });
          }
          break;

        case "list":
          if (token.items && token.items.length > 0) {
            const items = token.items.map((item) => {
              if (item.tokens && item.tokens.length > 0) {
                const allContent = [];
                item.tokens.forEach((subToken) => {
                  if (subToken.tokens) {
                    allContent.push(...processInlineTokensForPDF(subToken.tokens));
                  } else if (subToken.text) {
                    allContent.push({ text: subToken.text });
                  }
                });
                return allContent.length > 0 ? allContent : [{ text: item.text || "" }];
              }
              return [{ text: item.text || "" }];
            });
            segments.push({
              type: "list",
              ordered: token.ordered,
              items: items,
            });
          }
          break;

        case "code":
          if (token.text && token.text.trim()) {
            segments.push({
              type: "code",
              content: token.text,
              lang: token.lang,
            });
          }
          break;

        case "blockquote":
          if (token.tokens && token.tokens.length > 0) {
            token.tokens.forEach((subToken) => {
              if (subToken.type === "paragraph" && subToken.tokens) {
                segments.push({
                  type: "blockquote",
                  content: processInlineTokensForPDF(subToken.tokens),
                });
              }
            });
          } else if (token.text && token.text.trim()) {
            segments.push({
              type: "blockquote",
              content: [{ text: token.text }],
            });
          }
          break;

        case "table":
          if (token.header && token.rows) {
            const headers = token.header.map((cell) => {
              if (cell.tokens && cell.tokens.length > 0) {
                return processInlineTokensForPDF(cell.tokens);
              }
              return [{ text: cell.text || "" }];
            });

            const rows = token.rows.map((row) =>
              row.map((cell) => {
                if (cell.tokens && cell.tokens.length > 0) {
                  return processInlineTokensForPDF(cell.tokens);
                }
                return [{ text: cell.text || "" }];
              })
            );

            segments.push({
              type: "table",
              headers: headers,
              rows: rows,
              align: token.align || [],
            });
          }
          break;

        case "space":
          segments.push({ type: "space" });
          break;

        case "hr":
          segments.push({ type: "hr" });
          break;

        default:
          break;
      }
    });

    return segments;
  } catch (error) {
    console.error("Error parsing markdown for PDF:", error);
    return [{ type: "paragraph", content: [{ text: markdown }] }];
  }
};

// [Rest of the PDF rendering functions remain the same - renderFormattedText, etc.]
// ... (keeping all the existing PDF rendering logic)

const renderFormattedText = (doc, segments, startY, margin, textWidth, pageHeight) => {
  // ... (keep existing implementation)
  let yPos = startY;
  const lineHeight = 6;
  const pageMargin = 20;

  const checkPageBreak = (requiredSpace = 15) => {
    if (yPos + requiredSpace > pageHeight - pageMargin) {
      doc.addPage();
      return 20;
    }
    return yPos;
  };

  const renderContentSegments = (content, xOffset = 0, baseFontSize = 10, maxWidth = null) => {
    if (!content || content.length === 0) {
      yPos += lineHeight;
      return;
    }

    const effectiveMaxWidth = maxWidth || textWidth - xOffset;
    let currentX = margin + xOffset;
    let lineStartY = yPos;

    for (let i = 0; i < content.length; i++) {
      const segment = content[i];

      let fontStyle = "normal";
      if (segment.bold && segment.italic) fontStyle = "bolditalic";
      else if (segment.bold) fontStyle = "bold";
      else if (segment.italic) fontStyle = "italic";

      if (segment.code) {
        doc.setFont("courier", fontStyle);
        doc.setFontSize(baseFontSize - 1);
      } else {
        doc.setFont("helvetica", fontStyle);
        doc.setFontSize(baseFontSize);
      }

      // Set text color based on formatting (no special color for links)
      if (segment.code) {
        doc.setTextColor(139, 0, 0);
      } else {
        doc.setTextColor(0, 0, 0);
      }

      const words = segment.text.split(/(\s+)/);

      for (let j = 0; j < words.length; j++) {
        const word = words[j];
        if (!word) continue;

        const wordWidth = doc.getTextWidth(word);

        if (currentX + wordWidth > margin + textWidth && currentX > margin + xOffset) {
          yPos += lineHeight;
          yPos = checkPageBreak();
          currentX = margin + xOffset;
          lineStartY = yPos;
        }

        // Always render as plain text (no clickable links in PDF)
        doc.text(word, currentX, yPos);

        currentX += wordWidth;
      }
    }

    yPos += lineHeight;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
  };

  const convertSegmentsToText = (segments) => {
    if (!segments || segments.length === 0) return "";
    return segments.map((seg) => seg.text).join("");
  };

  segments.forEach((segment) => {
    switch (segment.type) {
      case "heading":
        yPos = checkPageBreak(25);
        const headingSizes = { 1: 18, 2: 16, 3: 14, 4: 12, 5: 11, 6: 10 };
        const fontSize = headingSizes[segment.level] || 12;
        yPos += segment.level === 1 ? 8 : 4;

        doc.setFontSize(fontSize);
        doc.setFont("helvetica", "bold");

        const headingText = segment.content.map((c) => c.text).join("");
        const headingLines = doc.splitTextToSize(headingText, textWidth);

        headingLines.forEach((line) => {
          doc.text(line, margin, yPos);
          yPos += fontSize * 0.5 + 2;
        });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        yPos += segment.level === 1 ? 6 : 4;
        break;

      case "paragraph":
        yPos = checkPageBreak();
        renderContentSegments(segment.content, 0, 10);
        yPos += 2;
        break;

      case "list":
        segment.items.forEach((item, idx) => {
          yPos = checkPageBreak(15);
          const bullet = segment.ordered ? `${idx + 1}. ` : "• ";

          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          const bulletWidth = doc.getTextWidth(bullet);
          doc.text(bullet, margin, yPos);

          const itemStartY = yPos;
          renderContentSegments(item, bulletWidth, 10, textWidth - bulletWidth);
        });
        yPos += 2;
        break;

      case "code":
        yPos = checkPageBreak(25);
        yPos += 3;

        let codeContent = segment.content;
        const charMap = {
          "├": "|",
          "│": "|",
          "└": "`",
          "─": "-",
          "┌": "+",
          "┐": "+",
          "┘": "+",
          "┴": "+",
          "┬": "+",
          "┤": "+",
          "┼": "+",
        };

        Object.keys(charMap).forEach((unicodeChar) => {
          codeContent = codeContent.replace(new RegExp(unicodeChar, "g"), charMap[unicodeChar]);
        });

        doc.setFont("courier", "normal");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.setFillColor(248, 248, 248);

        const codeLines = codeContent.split("\n");
        const codePadding = 4;
        const codeLineHeight = 4.5;

        const codeBlockHeight = codeLines.length * codeLineHeight + codePadding * 2;

        doc.rect(margin, yPos - codePadding + 1, textWidth, codeBlockHeight, "F");

        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.5);
        doc.rect(margin, yPos - codePadding + 1, textWidth, codeBlockHeight, "S");

        codeLines.forEach((line) => {
          if (yPos + codeLineHeight > pageHeight - pageMargin) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line || " ", margin + 3, yPos);
          yPos += codeLineHeight;
        });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        yPos += codePadding + 4;
        break;

      case "blockquote":
        yPos = checkPageBreak(15);
        const quoteStartY = yPos;
        const quotePadding = 12;

        doc.setFont("helvetica", "italic");
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);

        const quoteContentStartY = yPos;
        renderContentSegments(segment.content, quotePadding, 10, textWidth - quotePadding);

        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(3);
        doc.line(margin + 4, quoteContentStartY - 2, margin + 4, yPos - lineHeight);

        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        yPos += 3;
        break;

      case "table":
        yPos = checkPageBreak(30);

        const tableHeaders = segment.headers.map((header) => convertSegmentsToText(header));
        const tableRows = segment.rows.map((row) => row.map((cell) => convertSegmentsToText(cell)));

        const numColumns = tableHeaders.length;
        const columnStyles = {};

        if (numColumns === 2) {
          columnStyles[0] = { cellWidth: 30 };
          columnStyles[1] = { cellWidth: "auto" };
        } else if (numColumns === 3) {
          columnStyles[0] = { cellWidth: 25 };
          columnStyles[1] = { cellWidth: "auto" };
          columnStyles[2] = { cellWidth: "auto" };
        } else {
          for (let i = 0; i < numColumns; i++) {
            columnStyles[i] = { cellWidth: "auto" };
          }
        }

        autoTable(doc, {
          startY: yPos,
          head: [tableHeaders],
          body: tableRows,
          columnStyles: columnStyles,
          styles: {
            fontSize: 9,
            cellPadding: 4,
            overflow: "linebreak",
            cellWidth: "wrap",
            valign: "top",
            lineColor: [200, 200, 200],
            lineWidth: 0.1,
            minCellHeight: 10,
          },
          headStyles: {
            fontSize: 10,
            fontStyle: "bold",
            halign: "left",
            fillColor: [240, 240, 240],
            textColor: [0, 0, 0],
            cellPadding: 4,
          },
          bodyStyles: {
            textColor: [0, 0, 0],
          },
          alternateRowStyles: {
            fillColor: [250, 250, 250],
          },
          margin: { left: margin, right: margin },
          tableWidth: "auto",
          theme: "grid",
        });

        yPos = doc.lastAutoTable.finalY + 8;
        break;

      case "space":
        yPos += 5;
        break;

      case "hr":
        yPos = checkPageBreak(10);
        yPos += 2;
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, textWidth + margin, yPos);
        yPos += 8;
        break;

      default:
        break;
    }
  });

  return yPos;
};

// Helper function to filter threat catalog columns based on export options
const filterThreatCatalogColumns = (columns, columnOptions) => {
  // Map column keys (from data) to option keys (from modal)
  const columnMap = {
    'name': 'threatName',
    'stride_category': 'strideCategory',
    'description': 'description',
    'target': 'threatId',  // "target" maps to "Threat ID" option
    'impact': 'impact',
    'likelihood': 'likelihood',
    'risk_score': 'riskScore',
    'iso_controls': 'iso27001',
    'mitigations': 'mitigations',
  };

  return columns.filter((col) => {
    const optionKey = columnMap[col];
    // If column is in map, check if it's selected; otherwise include it
    return optionKey ? columnOptions[optionKey] : true;
  });
};

// Helper function to filter threat catalog data based on selected columns
const filterThreatCatalogData = (data, originalColumns, filteredColumns) => {
  if (!data || data.length === 0) return data;
  
  // Just return the original data - the table renderer will use filteredColumns
  // to determine which columns to display
  return data;
};

export const createThreatModelingPDF = async (
  architectureDiagramBase64,
  title,
  description,
  assumptions,
  assets,
  dataFlowData,
  trustBoundaryData,
  threatSourceData,
  threatCatalogData,
  smeComparisonFlags = [],  // NEW: SME comparison flags
  options = null // Export options for filtering
) => {
  const doc = new jsPDF();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  const textWidth = pageWidth - margin * 2;

  // Default options if not provided (include everything)
  const exportOptions = options || {
    sections: {
      architectureDiagram: true,
      assets: true,
      dataFlow: true,
      trustBoundary: true,
      threatSource: true,
      threatCatalog: true,
    },
    columns: {
      threatId: true,
      threatName: true,
      description: true,
      strideCategory: true,
      impact: true,
      likelihood: true,
      riskScore: true,
      iso27001: true,
      mitigations: true,
    },
    riskScoreFilter: {
      High: true,
      'Medium/High': true,
      Medium: true,
      'Low/Medium': true,
      Low: true,
    },
  };

  // Filter threats by risk score if filter is provided
  const filteredThreats = threatCatalogData && exportOptions.riskScoreFilter
    ? threatCatalogData.filter(threat => exportOptions.riskScoreFilter[threat.risk_score])
    : threatCatalogData;

  // Sort threats by risk score (High to Low) - same order as UI
  const sortedThreats = filteredThreats ? [...filteredThreats].sort((a, b) => {
    const riskScoreOrder = { 
      'High': 5, 
      'Medium/High': 4, 
      'Medium': 3, 
      'Low/Medium': 2, 
      'Low': 1 
    };
    const scoreA = riskScoreOrder[a?.risk_score] || 0;
    const scoreB = riskScoreOrder[b?.risk_score] || 0;
    return scoreB - scoreA; // Descending order (High first)
  }) : [];

  // Table of Contents tracking
  const tocEntries = [];
  const addTOCEntry = (sectionTitle, level = 1) => {
    const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
    // Don't subtract 1 here - we'll handle the correction when displaying TOC
    tocEntries.push({ title: sectionTitle, page: currentPage, level });
  };

  // TUI Brand Colors
  const TUI_BLUE = [0, 115, 187];      // #0073BB
  const TUI_GRAY = [84, 91, 100];      // #545B64
  const LIGHT_GRAY = [204, 204, 204];  // #CCCCCC
  const BLACK = [0, 0, 0];             // #000000

  // Start TOC page with consistent top margin
  let yPos = 20;

  // Title Section - Enhanced with TUI branding
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...TUI_BLUE);
  doc.text("THREAT MODELING REPORT", pageWidth / 2, yPos, { align: "center" });
  
  yPos += 10;
  
  // Subtitle - Report ID
  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...TUI_GRAY);
  doc.text(title, pageWidth / 2, yPos, { align: "center" });
  
  yPos += 25;

  // Table of Contents Header - Enhanced styling
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BLACK);
  doc.text("Table of Contents", margin, yPos);
  
  // TUI Blue underline accent
  const tocHeaderWidth = doc.getTextWidth("Table of Contents");
  doc.setDrawColor(...TUI_BLUE);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos + 2, margin + tocHeaderWidth, yPos + 2);
  
  yPos += 15;

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...BLACK);

  // Reserve space for TOC - we'll fill this in later
  const tocStartY = yPos;
  yPos += 120; // Reserve space for TOC entries

  // Start content on page 2
  doc.addPage();
  yPos = 20;

  // Architecture Diagram(s) (conditional)
  if (exportOptions.sections.architectureDiagram && architectureDiagramBase64) {
    // Handle both single diagram (string) and multiple diagrams (array)
    const diagrams = Array.isArray(architectureDiagramBase64) 
      ? architectureDiagramBase64 
      : [architectureDiagramBase64];

    for (let index = 0; index < diagrams.length; index++) {
      try {
        let imageData = diagrams[index];
        
        // Handle different formats:
        // 1. Object with type and value: {type: "image/png", value: "base64string"}
        // 2. Plain base64 string: "base64string"
        // 3. Data URI: "data:image/png;base64,base64string"
        if (typeof imageData === "object" && imageData?.value) {
          imageData = `data:${imageData.type};base64,${imageData.value}`;
        } else if (typeof imageData === "string" && !imageData.startsWith("data:")) {
          // Plain base64 string - add data URI prefix
          imageData = `data:image/png;base64,${imageData}`;
        }

        if (yPos > pageHeight - 80) {
          doc.addPage();
          yPos = 20;
        }

        // Add section title (with number if multiple diagrams)
        const title = diagrams.length > 1 
          ? `${SECTION_TITLES.ARCHITECTURE_DIAGRAM} ${index + 1}`
          : SECTION_TITLES.ARCHITECTURE_DIAGRAM;
        
        addTOCEntry(title);  // Track for TOC
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(title, margin, yPos);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        yPos += 10;

        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageData;
        });

        const maxImgWidth = textWidth;
        const maxImgHeight = pageHeight - yPos - 30;

        let imgWidth = img.width;
        let imgHeight = img.height;

        if (imgWidth > maxImgWidth) {
          const scale = maxImgWidth / imgWidth;
          imgWidth = maxImgWidth;
          imgHeight = imgHeight * scale;
        }

        if (imgHeight > maxImgHeight) {
          const scale = maxImgHeight / imgHeight;
          imgHeight = maxImgHeight;
          imgWidth = imgWidth * scale;
        }

        if (yPos + imgHeight > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }

        doc.addImage(imageData, "JPEG", margin, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 12;
      } catch (error) {
        // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
        console.error(`Error adding architecture diagram ${index + 1} to PDF:`, error);
        doc.setFontSize(10);
        doc.setTextColor(255, 0, 0);
        doc.text(`Error: Could not load architecture diagram ${index + 1}`, margin, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 10;
      }
    }
  }

  // Helper functions using shared utilities
  const addMarkdownSection = (sectionTitle, markdown) => {
    if (!markdown) return;

    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = 20;
    }

    addTOCEntry(sectionTitle);  // Track for TOC
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(sectionTitle, margin, yPos);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    yPos += 10;

    const segments = parseMarkdownForPDF(markdown);
    yPos = renderFormattedText(doc, segments, yPos, margin, textWidth, pageHeight);
    yPos += 6;
  };

  const addTableSection = (sectionTitle, data, columns, forceLandscape = false) => {
    if (!data || data.length === 0) return;

    if (forceLandscape) {
      doc.addPage("a4", "landscape");
      const landscapeWidth = doc.internal.pageSize.getWidth();
      const landscapeTextWidth = landscapeWidth - margin * 2;
      yPos = 20;

      addTOCEntry(sectionTitle);  // Track for TOC
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(sectionTitle, margin, yPos);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      yPos += 10;

      // Better column width distribution for landscape tables
      const numColumns = columns.length;
      const columnStyles = {};

      // Special handling for threat catalog (7 columns)
      if (numColumns === 7) {
        columnStyles[0] = { cellWidth: 40 }; // name
        columnStyles[1] = { cellWidth: 28 }; // stride_category
        columnStyles[2] = { cellWidth: 60 }; // description
        columnStyles[3] = { cellWidth: 30 }; // target
        columnStyles[4] = { cellWidth: 22 }; // impact
        columnStyles[5] = { cellWidth: 22 }; // likelihood
        columnStyles[6] = { cellWidth: 68 }; // mitigations (reduced from 78)
      } else if (numColumns === 9) {
        // With risk_score and iso_controls - optimized widths
        columnStyles[0] = { cellWidth: 34 }; // name
        columnStyles[1] = { cellWidth: 22 }; // stride_category
        columnStyles[2] = { cellWidth: 50 }; // description
        columnStyles[3] = { cellWidth: 24 }; // target
        columnStyles[4] = { cellWidth: 28 }; // impact (increased - "High Confidentiality" fits)
        columnStyles[5] = { cellWidth: 14 }; // likelihood (reduced - just "High")
        columnStyles[6] = { cellWidth: 14 }; // risk_score (reduced - just "High")
        columnStyles[7] = { cellWidth: 22 }; // iso_controls (reduced)
        columnStyles[8] = { cellWidth: 62 }; // mitigations (increased for URLs)
      } else {
        // Default auto for other landscape tables
        for (let i = 0; i < numColumns; i++) {
          columnStyles[i] = { cellWidth: "auto" };
        }
      }

      autoTable(doc, {
        startY: yPos,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          overflow: "linebreak",
          cellWidth: "wrap",
          valign: "top",
          halign: "left",
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        columnStyles: columnStyles,
        headStyles: {
          fontSize: 9,
          fontStyle: "bold",
          halign: "left",
          cellPadding: 3,
          fillColor: [66, 139, 202],
          textColor: [255, 255, 255],
          valign: "middle",
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          minCellHeight: 12,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        head: [columns.map(formatTableHeader)],
        body: data.map((row) =>
          columns.map((col) => {
            let value = Array.isArray(row[col]) ? formatArrayCellContent(row[col]) : row[col] || "";
            
            // Special formatting for Impact column
            if (col === 'impact') {
              value = formatImpactForPDF(value);
            }
            
            // Add SME comparison to description if flag exists (only for threat catalog)
            if (col === 'description' && sectionTitle === SECTION_TITLES.THREAT_CATALOG && smeComparisonFlags && smeComparisonFlags.length > 0) {
              const smeFlag = smeComparisonFlags.find(
                flag => flag.ai_threat_name === row.name && flag.ai_threat_target === row.target
              );
              
              if (smeFlag) {
                const smeSection = `\n\n--- SME Comparison ---\nAI Rating: ${smeFlag.ai_rating} | SME Rating: ${smeFlag.sme_rating}\nSimilar SME Threat: "${smeFlag.similar_sme_threat}"\nSME Rationale: ${smeFlag.sme_rationale || 'N/A'}\nRecommendation: ${smeFlag.recommendation || 'N/A'}`;
                value = value + smeSection;
              }
            }
            
            return typeof value === 'string' ? sanitizeTextForPDF(value) : value;
          })
        ),
        // Disable auto-linking of URLs in PDF cells
        didDrawCell: (data) => {
          // Remove any links that jspdf-autotable auto-created
          data.cell.text.forEach((line) => {
            if (typeof line === 'object' && line.link) {
              delete line.link;
            }
          });
        },
        margin: { left: margin, right: margin },
        tableWidth: "auto",
        theme: "grid",
      });

      yPos = doc.lastAutoTable.finalY + 10;
    } else {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      addTOCEntry(sectionTitle);  // Track for TOC
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(sectionTitle, margin, yPos);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      yPos += 10;

      // Better column width distribution for portrait tables
      const numColumns = columns.length;
      const columnStyles = {};

      if (numColumns === 1) {
        // Single column (like assumptions)
        columnStyles[0] = { cellWidth: textWidth };
      } else if (numColumns === 2) {
        columnStyles[0] = { cellWidth: 40 };
        columnStyles[1] = { cellWidth: textWidth - 40 };
      } else if (numColumns === 3) {
        // For assets table: type, name, description
        columnStyles[0] = { cellWidth: 30 }; // type
        columnStyles[1] = { cellWidth: 40 }; // name
        columnStyles[2] = { cellWidth: textWidth - 70 }; // description
      } else {
        // Default distribution for other tables
        const equalWidth = textWidth / numColumns;
        for (let i = 0; i < numColumns; i++) {
          columnStyles[i] = { cellWidth: equalWidth };
        }
      }

      autoTable(doc, {
        startY: yPos,
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
          cellWidth: "wrap",
          valign: "top",
          halign: "left",
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        columnStyles: columnStyles,
        headStyles: {
          fontSize: 10,
          fontStyle: "bold",
          halign: "left",
          cellPadding: 3,
          fillColor: [66, 139, 202],
          textColor: [255, 255, 255],
          valign: "middle",
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          minCellHeight: 12,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        head: [columns.map(formatTableHeader)],
        body: data.map((row) =>
          columns.map((col) => {
            let value = Array.isArray(row[col]) ? formatArrayCellContent(row[col]) : row[col] || "";
            
            // Special formatting for Impact column
            if (col === 'impact') {
              value = formatImpactForPDF(value);
            }
            
            return typeof value === 'string' ? sanitizeTextForPDF(value) : value;
          })
        ),
        // Disable auto-linking of URLs in PDF cells
        didDrawCell: (data) => {
          // Remove any links that jspdf-autotable auto-created
          data.cell.text.forEach((line) => {
            if (typeof line === 'object' && line.link) {
              delete line.link;
            }
          });
        },
        margin: { left: margin, right: margin },
        tableWidth: "auto",
        theme: "grid",
      });

      yPos = doc.lastAutoTable.finalY + 10;
    }
  };

  // Add all sections using shared configuration
  const sections = getDocumentSections({
    description,
    assumptions,
    assets,
    dataFlowData,
    trustBoundaryData,
    threatSourceData,
    threatCatalogData: sortedThreats, // Use sorted and filtered threats
  });

  // Filter sections based on export options
  const filteredSections = sections.filter((section) => {
    const sectionKey = section.title.toLowerCase().replace(/\s+/g, '');
    
    // Map section titles to option keys
    const sectionMap = {
      'assets': 'assets',
      'dataflow': 'dataFlow',
      'trustboundary': 'trustBoundary',
      'threatsource': 'threatSource',
      'threatcatalog': 'threatCatalog',
    };
    
    const optionKey = sectionMap[sectionKey];
    return optionKey ? exportOptions.sections[optionKey] : true;
  });

  filteredSections.forEach((section) => {
    if (section.type === "text") {
      addMarkdownSection(section.title, section.content);
    } else if (section.type === "table") {
      // Filter columns for Threat Catalog
      if (section.title === SECTION_TITLES.THREAT_CATALOG) {
        const filteredColumns = filterThreatCatalogColumns(section.columns, exportOptions.columns);
        addTableSection(section.title, section.data, filteredColumns, section.landscape);
      } else {
        addTableSection(section.title, section.data, section.columns, section.landscape);
      }
    }
  });

  // Fill in Table of Contents on page 1 with corrected page numbers
  if (tocEntries.length > 0) {
    doc.setPage(1);
    yPos = tocStartY;

    // TUI Brand Colors for TOC entries
    const TUI_BLUE = [0, 115, 187];
    const BLACK = [0, 0, 0];
    const LIGHT_GRAY = [204, 204, 204];

    tocEntries.forEach((entry, index) => {
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
      
      const indent = (entry.level - 1) * 10;
      
      // Page numbering:
      // Display shows entry.page - 1 (so page 2 shows as "1")
      // Link uses entry.page (actual page, 1-based in jsPDF)
      const displayPageNumber = String(entry.page - 1);
      const linkPageNumber = entry.page; // Don't subtract - jsPDF uses 1-based pages
      
      // Section title (left-aligned, clickable)
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...BLACK);
      const titleX = margin + indent;
      const titleWidth = doc.getTextWidth(entry.title);
      doc.text(entry.title, titleX, yPos);
      
      // Make title clickable
      doc.link(titleX, yPos - 5, titleWidth, 10, { pageNumber: linkPageNumber });
      
      // Page number (right-aligned, bold, clickable)
      doc.setTextColor(...BLACK);
      doc.setFont("helvetica", "bold");
      const pageNumWidth = doc.getTextWidth(displayPageNumber);
      const pageNumX = pageWidth - margin - pageNumWidth;
      doc.text(displayPageNumber, pageNumX, yPos);
      
      // Make page number clickable
      doc.link(pageNumX, yPos - 5, pageNumWidth, 10, { pageNumber: linkPageNumber });
      
      // Consistent dot leaders
      const startX = titleX + titleWidth + 5;
      const endX = pageNumX - 5;
      const dotsWidth = endX - startX;
      
      if (dotsWidth > 10) {
        // Create evenly spaced dots with consistent spacing
        const dotSpacing = 4;
        const numDots = Math.floor(dotsWidth / dotSpacing);
        let dotString = '';
        for (let i = 0; i < numDots; i++) {
          dotString += '.';
        }
        
        doc.setTextColor(...LIGHT_GRAY);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.text(dotString, startX, yPos);
      }
      
      // Reset to defaults
      doc.setTextColor(...BLACK);
      doc.setFont("helvetica", "normal");
      
      yPos += 12; // Consistent spacing between entries
    });
  }

  return doc;
};
